import {
  ImageField,
  ImageFieldImage,
  KeyTextField,
  RichTextField,
} from '@prismicio/client';
import {
  AddressFormSlice,
  ICollection,
  ICustomerAddress,
  MultiSelectCollectionSlice,
  MultiStepFormDocumentDataStepsItem,
  UnknownObject,
} from '@monofront/types';
import { MachineConfig } from '@monofront/xstate-v4';

export enum MultiStepFormStepType {
  MultiSelectCollection = 'MultiSelectCollection',
  AddressForm = 'AddressForm',
  Intro = 'Intro',
  SummaryStep = 'SummaryStep',
}

export type MultiStepFormStep =
  | {
      id: string;
      uid: string;
      header:
        | {
            type: MultiStepFormHeaderType.NONE;
          }
        | {
            type: MultiStepFormHeaderType.STEPPER;
            style: { backgroundColor: string | undefined };
          }
        | {
            type: MultiStepFormHeaderType.BANNER;
            isBottomBorderCurved: boolean;
            image: ImageFieldImage<'filled'>;
          };
      title: RichTextField<'filled'> | null;
      subtitle: RichTextField<'filled'> | null;
      body: Array<MultiSelectCollectionSlice | AddressFormSlice>;
      style: {
        container: {
          backgroundColor: string | undefined;
        };
        bottomInset: {
          backgroundColor: string | undefined;
        };
      };
      nextCTAText: string | null;
      closeCTAText: string | null;
    } & (
      | {
          type: MultiStepFormStepType.MultiSelectCollection;
          collections: Array<{
            collection: ICollection;
            image: ImageField;
            title: string;
          }>;
        }
      | { type: MultiStepFormStepType.AddressForm }
      | { type: MultiStepFormStepType.Intro }
      | { type: MultiStepFormStepType.SummaryStep }
    );

export type MultiStepForm = {
  steps: Record<string, MultiStepFormStep>;
};

export type FirstOrderFormStep =
  | { id: 'page_loader' }
  | ({
      id: string;
      data: MultiStepFormDocumentDataStepsItem;
      header:
        | {
            type: MultiStepFormHeaderType.NONE;
          }
        | {
            type: MultiStepFormHeaderType.STEPPER;
            style: { backgroundColor: string | undefined };
          }
        | {
            type: MultiStepFormHeaderType.BANNER;
            isBottomBorderCurved: boolean;
            image: ImageField<'filled'>;
          };
      title: RichTextField<'filled'> | null;
      subtitle: KeyTextField<'filled'> | null;
      body: MultiSelectCollectionSlice[];
      style: {
        container: {
          backgroundColor: string | undefined;
        };
        bottomInset: {
          backgroundColor: string | undefined;
        };
      };
      nextCTAText: string | null;
      closeCTAText: string | null;
    } & (
      | {
          type: 'MultiSelectCollection';
          collections: {
            collection: ICollection;
            image: ImageField;
            title: string;
          };
        }
      | { type: 'AddressForm' }
      | { type: 'Intro' }
      | { type: 'SummaryStep' }
    ));

export enum MultiStepFormHeaderType {
  NONE = 'NONE',
  STEPPER = 'STEPPER',
  BANNER = 'BANNER',
}

export enum ACTIONS {
  INIT_FORM_DATA = 'initFormData',
  SAVE_FORM_DATA = 'saveFormData',
  START_FORM = 'startForm',
  EXIT_FORM = 'exitForm',
  FINISH_FORM = 'finishForm',
  NEXT = 'NEXT',
  PREVIOUS = 'PREVIOUS',
  SELECT_ADDRESS = 'SELECT_ADDRESS',
}

export enum TAGS {
  FORM_STARTED = 'first_order_form_started',
  FORM_FINISHED = 'first_order_form_finished',
}

export const FORM_NAME = '1st order form';

export type SelectCollectionEvent = {
  displayed: string[];
  added: string[];
  removed: string[];
};
export type AddressFormEvent = {
  street: string;
  city: string;
  postcode: string;
  country: string;
  // label: string;
};

/********************************************
 * EVENTS
 ********************************************/
export type InitFormDataEvent = {
  type: ACTIONS.INIT_FORM_DATA;
  data: {
    savedCollections: Array<string>;
    savedAddress: ICustomerAddress;
  };
};

export type StartFormEvent = {
  type: ACTIONS.START_FORM;
};

export type NextEvent = {
  type: ACTIONS.NEXT;
  payload: UnknownObject;
};

export type PreviousEvent = {
  type: ACTIONS.PREVIOUS;
  payload: UnknownObject;
};

export type SaveFormDataEvent = {
  type: ACTIONS.SAVE_FORM_DATA;
  payload: MultiSelectStepContextData;
};

export type ExitFormEvent = {
  type: ACTIONS.EXIT_FORM;
};

export type FinishFormEvent = {
  type: ACTIONS.FINISH_FORM;
  payload: MultiSelectStepContextData;
};

export type SelectAddressEvent = {
  type: ACTIONS.SELECT_ADDRESS;
  payload: AddressFormEvent;
};

export type Events =
  | InitFormDataEvent
  | StartFormEvent
  | SaveFormDataEvent
  | ExitFormEvent
  | FinishFormEvent
  | NextEvent
  | PreviousEvent
  | SelectAddressEvent;

/********************************************
 * CONTEXT
 ********************************************/
export type StepContextData = {
  stepId: string;
};
export type MultiSelectStepContextData = StepContextData & {
  collections: Array<string>;
  address: AddressFormStepContextData;
};
export type AddressFormStepContextData = {
  street: string;
  city: string;
  postcode: string;
  country: string;
  // label: string;
};
export type Context = {
  firstStepID: string;
  steps: Record<string, MultiSelectStepContextData>;
  savedCollections: Array<string>;
};

export type MultiStepFormMachineConfig = MachineConfig<Context, any, Events>;
